import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { setError } from './errorSlice';
import { addNotification } from './notificationSlice';
import { startLoading, stopLoading } from './loadingSlice';

interface SubscriptionToApprove {
  id: string;
  name?: string;
}

interface SubscriptionToApproveOverageListResponse {
  subscriptions_to_approve: SubscriptionToApprove[];
}

interface OverageApprovalResponse {
  approval_url?: string;
  message?: string;
}

interface OverageState {
  pendingSubscriptions: SubscriptionToApproveOverageListResponse | null;
  loading: boolean;
  error: string | null;
  approvalLoading: boolean;
  approvalError: string | null;
  approvalResultModal: {
    isVisible: boolean;
    result: 'success' | 'error' | null;
  };
}

const initialState: OverageState = {
  pendingSubscriptions: null,
  loading: false,
  error: null,
  approvalLoading: false,
  approvalError: null,
  approvalResultModal: {
    isVisible: false,
    result: null,
  },
};

export const fetchPendingOverage = createAsyncThunk(
  'overage/fetchPendingOverage',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response: SubscriptionToApproveOverageListResponse = { subscriptions_to_approve: [] };
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch pending overage';
      dispatch(setError(errorMessage));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const approveOverage = createAsyncThunk(
  'overage/approveOverage',
  async (subscriptionId: string, { dispatch }) => {
    dispatch(startLoading());
    try {
      const response: OverageApprovalResponse = { message: 'Not implemented yet' };
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve overage';
      dispatch(setError(errorMessage));
      dispatch(addNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      }));
      throw err;
    } finally {
      dispatch(stopLoading());
    }
  }
);

const overageSlice = createSlice({
  name: 'overage',
  initialState,
  reducers: {
    clearOverageError: (state) => {
      state.error = null;
      state.approvalError = null;
    },
    clearPendingOverage: (state) => {
      state.pendingSubscriptions = null;
    },
    showApprovalResultModal: (state, action: PayloadAction<{ result: 'success' | 'error' }>) => {
      state.approvalResultModal = {
        isVisible: true,
        result: action.payload.result,
      };
    },
    hideApprovalResultModal: (state) => {
      state.approvalResultModal = {
        isVisible: false,
        result: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchPendingOverage
      .addCase(fetchPendingOverage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingOverage.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingSubscriptions = action.payload;
      })
      .addCase(fetchPendingOverage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch pending overage';
      })
      // approveOverage
      .addCase(approveOverage.pending, (state) => {
        state.approvalLoading = true;
        state.approvalError = null;
      })
      .addCase(approveOverage.fulfilled, (state) => {
        state.approvalLoading = false;
      })
      .addCase(approveOverage.rejected, (state, action) => {
        state.approvalLoading = false;
        state.approvalError = action.error.message || 'Failed to approve overage';
      });
  },
});

export const { 
  clearOverageError, 
  clearPendingOverage, 
  showApprovalResultModal, 
  hideApprovalResultModal 
} = overageSlice.actions;
export default overageSlice.reducer;
